import { captureException } from "@sentry/react";
import { MINUTES_IN_HOUR } from "emoreg/organisms/calendar/constants/time-constants";
import { upperFirst } from "lodash";
import { DateTime, Duration, DurationObject } from "luxon";

import { handlePlural } from "./formatters";

export const DAILY = "DAILY";
export const WEEKLY = "WEEKLY";
export const MONTHLY = "MONTHLY";
export const YEARLY = "YEARLY";
export const ALL_TIME = "ALL_TIME";

const PERIODS = [DAILY, WEEKLY, MONTHLY, YEARLY, ALL_TIME];

/** Duration of one period, keyed by period type (ALL_TIME has none). */
const PERIOD_DURATIONS: Record<string, DurationObject> = {
  [DAILY]: { days: 1 },
  [WEEKLY]: { weeks: 1 },
  [MONTHLY]: { months: 1 },
  [YEARLY]: { years: 1 },
};

/** Unit passed to luxon `startOf` / `endOf` for each bounded period type. */
const PERIOD_UNITS: Record<string, "day" | "week" | "month" | "year"> = {
  [DAILY]: "day",
  [WEEKLY]: "week",
  [MONTHLY]: "month",
  [YEARLY]: "year",
};

export const DISPLAY_DATE_FORMAT = "dd/MM/yyyy";
export const DISPLAY_TIME_DATE_FORMAT = "HH'h'mm - dd/MM/yyyy";
export const DISPLAY_DATETIME_FORMAT = "dd/MM/yyyy 'à' HH'h'mm";
export const DISPLAY_SHORT_DATETIME_FORMAT = "dd/MM/yyyy HH:mm";
export const DISPLAY_MONTH_FORMAT = "MMMM yyyy";
export const DISPLAY_SHORT_DAY = "d";
export const DISPLAY_SHORT_MONTH_FORMAT = "MM/yyyy";
export const DISPLAY_SHORT_DATE_FORMAT = "dd/MM/yyyy";
export const DISPLAY_SHORT_TIME_FORMAT = "HH:mm";

export const INPUT_MONTH_FORMAT = "yyyy-MM";
export const INPUT_DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm";
export const INPUT_DATE_FORMAT = "yyyy-MM-dd";

const assertKnownPeriodType = (periodType: string): void => {
  if (!PERIODS.includes(periodType)) {
    throw new Error(`Unrecognized period type: ${periodType}`);
  }
};

const assertBoundedPeriodType = (periodType: string): void => {
  assertKnownPeriodType(periodType);
  if (periodType === ALL_TIME) {
    throw new Error("ALL_TIME period has no bounds");
  }
};

const assertPeriodDate = (date: unknown): void => {
  if (!DateTime.isDateTime(date)) {
    throw new Error("Unrecognized `date`");
  }
  // Luxon never throws: an invalid DateTime would silently flow through
  // startOf / plus and end up as `null` when serialized.
  if (!date.isValid) {
    throw new Error(`Invalid \`date\`: ${date.invalidReason}`);
  }
};

/**
 * Parse a reference date coming from a URL or from stored filters.
 * Falls back to today when the value is missing or unparsable, so a broken URL
 * never turns into `null` bounds sent to the API.
 */
export const parseReferenceDate = (value: string | null | undefined): DateTime => {
  const parsed = value ? DateTime.fromISO(value) : null;
  return parsed?.isValid ? parsed : DateTime.local();
};

/**
 * Return the label corresponding to a period.
 * @param periodType - A known period type (see `hassibot.util.date.PERIODS`)
 * @param date - A date inside the wanted period
 * @returns Label corresponding to the given period type and date
 */
export function periodLabel(periodType: string, date: DateTime): string {
  if (!PERIODS.includes(periodType)) {
    captureException(new Error(`Unrecognized period type: ${periodType}`));
    return ALL_TIME;
  }

  if (periodType === ALL_TIME) {
    return ALL_TIME;
  }

  assertPeriodDate(date);

  switch (periodType) {
    case DAILY:
      return date.toFormat("yyyy-MM-dd");
    case WEEKLY:
      // ISO week year + ISO week number, e.g. "2024-W01"
      return date.toFormat("kkkk-'W'WW");
    case MONTHLY:
      return date.toFormat("yyyy-MM");
    default:
      return date.toFormat("yyyy");
  }
}

/**
 * Return the humanly readable string corresponding to the start and the end of a period.
 * @param periodType - A known period type (see `hassibot.util.date.PERIODS`)
 * @param date - A date inside the wanted period
 * @returns Label corresponding to the given period type and date
 */
export function periodHumanDates(periodType: string, date: DateTime): string {
  assertKnownPeriodType(periodType);

  if (periodType === ALL_TIME) {
    return "Depuis le tout début";
  }

  assertPeriodDate(date);

  if (periodType === DAILY) {
    return date.toFormat(DISPLAY_DATE_FORMAT);
  }

  const firstDay = firstDayOfPeriod(periodType, date);
  const lastDay = lastDayOfPeriod(periodType, date);
  return `${firstDay.toFormat(DISPLAY_DATE_FORMAT)} - ${lastDay.toFormat(DISPLAY_DATE_FORMAT)}`;
}

/**
 * Return the first day (at midnight) of the given period.
 * @param periodType - A bounded period type (DAILY, WEEKLY, MONTHLY or YEARLY)
 * @param date - A date inside the wanted period
 */
export function firstDayOfPeriod(periodType: string, date: DateTime): DateTime {
  assertBoundedPeriodType(periodType);
  assertPeriodDate(date);

  // Luxon weeks are ISO weeks, so `startOf("week")` is the Monday.
  return date.startOf(PERIOD_UNITS[periodType]);
}

/**
 * Return the last day (at midnight) of the given period.
 * @param periodType - A bounded period type (DAILY, WEEKLY, MONTHLY or YEARLY)
 * @param date - A date inside the wanted period
 */
export function lastDayOfPeriod(periodType: string, date: DateTime): DateTime {
  assertBoundedPeriodType(periodType);
  assertPeriodDate(date);

  return date.endOf(PERIOD_UNITS[periodType]).startOf("day");
}

/**
 * Return the first day of the next period.
 * @param periodType - A known period type (see `hassibot.util.date.PERIODS`)
 * @param date - A date inside the reference period
 */
export function firstDayOfNextPeriod(periodType: string, date: DateTime): DateTime {
  assertKnownPeriodType(periodType);

  if (periodType === ALL_TIME) {
    return DateTime.local(2090, 6, 27);
  }

  return firstDayOfPeriod(periodType, date).plus(PERIOD_DURATIONS[periodType]);
}

/**
 * Return the first day of the previous period if not before the 01/06/2016,
 * otherwise return null.
 * @param periodType - A known period type (see `hassibot.util.date.PERIODS`)
 * @param date - A date inside the reference period
 */
export function firstDayOfPreviousPeriod(periodType: string, date: DateTime): DateTime | null {
  assertKnownPeriodType(periodType);

  if (periodType === ALL_TIME) {
    return null;
  }

  const previousFirstDay = firstDayOfPeriod(periodType, date).minus(PERIOD_DURATIONS[periodType]);

  // Compare calendar dates only, whatever the time of day
  return previousFirstDay.toFormat("yyyyMMdd") >= "20160601" ? previousFirstDay : null;
}

export const periodTypeToLabelMap = {
  WEEKLY: "Hebdomadaire",
  MONTHLY: "Mensuel",
  DAILY: "Quotidien",
  ALL_TIME: "Tout",
};

export const humanShortDate = (dt, format = "dd/MM") => {
  const datish = dt.startOf("day");
  const today = DateTime.local().startOf("day");
  const yesterday = today.minus({ days: 1 }).startOf("day");
  const tomorrow = today.plus({ days: 1 }).startOf("day");
  if (+datish === +yesterday) {
    return "hier";
  } else if (+datish === +today) {
    return "aujourd'hui";
  } else if (+datish === +tomorrow) {
    return "demain";
  }
  return dt.toFormat(format);
};

export const humanShortDatetime = (dt, date_format = "dd/MM", time_format = "hh:mm") => {
  const date = humanShortDate(dt, date_format);
  const time = dt.toFormat(time_format);
  return `${date}${time}`;
};

type FormatRelativeDateOptions = {
  capitalize?: boolean;
  format?: string;
  short?: boolean;
};

export const formatRelativeDate = (date: DateTime, options?: FormatRelativeDateOptions): string => {
  const { capitalize = true, format = "dd LLL yyyy", short = false } = options ?? {};
  const today = DateTime.now().startOf("day");
  const targetDate = date.startOf("day");

  if (targetDate.equals(today)) {
    if (short) {
      return capitalize ? "Ajd" : "ajd";
    }
    return capitalize ? "Aujourd'hui" : "aujourd'hui";
  }

  if (targetDate.equals(today.minus({ days: 1 }))) {
    return capitalize ? "Hier" : "hier";
  }

  return date.toFormat(format, { locale: "fr" });
};

export const formatTotalExperience = (year: number): string => {
  const month = year * 12;
  return monthsToYear(month);
};

export const monthsToYear = (seniority: number): string => {
  const year = Math.trunc(seniority / 12);
  const month = seniority % 12;
  if (year > 0) {
    return year + ` an${year > 1 ? "s" : ""} ${month > 0 ? month + " mois" : ""}`;
  } else if (month > 0) {
    return month + " mois";
  } else {
    return "< 1 mois";
  }
};

export const formatInterventionDuration = (
  startAt?: DateTime | null,
  endAt?: DateTime | null
): string => {
  if (startAt && endAt) {
    const duration = endAt.diff(startAt, ["hours", "minutes"]);
    return duration.toFormat("h'h'mm");
  } else return "?";
};

export const isMidnightDatetime = (datetime: DateTime): boolean => {
  return datetime.get("hour") === 0 && datetime.get("minute") === 0 && datetime.get("second") === 0;
};

export const isNoonDatetime = (datetime: DateTime): boolean => {
  return (
    datetime.get("hour") === 12 && datetime.get("minute") === 0 && datetime.get("second") === 0
  );
};

export const getHoursAndMinutes = (minutes: number) => {
  const hours = Math.floor(minutes / MINUTES_IN_HOUR);
  const remainingMinutes = minutes % MINUTES_IN_HOUR;
  return { hours, minutes: remainingMinutes };
};

/**
 * @description This function is taking hour and minutes and format it with luxon tokens. We are preventing 00min and 00h10min
 */
export const hourMinutesToString = (hours?: number, minutes?: number) => {
  const templateStringHours = hours && hours > 0 ? "h'h'" : "";
  const templateStringMinutes = minutes && minutes > 0 ? "mm'min'" : "";
  const templateString = [templateStringHours, templateStringMinutes].join("");

  return Duration.fromObject({
    hours: hours,
    minutes: minutes,
  }).toFormat(templateString);
};

/**
 * @description Convert a date to the iso datetime of its midnight. Optionnally add days to initial date.
 */
const dateToIsoDatetime = (date: DateTime, daysNumberToAdd = 0): string =>
  date.plus({ days: daysNumberToAdd }).startOf("day").toISO();

/**
 * @description Get filter datetime range from a reference date and a period's type ("DAILY" | "WEEKLY" | "MONTHLY")
 */
export const getFilterDatetimeRange = (
  periodType: string | undefined,
  referenceDate: string | undefined
): [string, string] => {
  const reference = parseReferenceDate(referenceDate);
  const firstDay = firstDayOfPeriod(periodType ?? "", reference);
  const lastDay = lastDayOfPeriod(periodType ?? "", reference);

  return [dateToIsoDatetime(firstDay), dateToIsoDatetime(lastDay, 1)];
};

/**
 * @description Returns the number of days elapsed since the provided datetime
 */
export const getDaysSince = (datetime: DateTime): number =>
  DateTime.now().diff(datetime).as("days");

/**
 * @description Returns the number of months elapsed since the provided datetime
 */
const getMonthsSince = (datetime: DateTime): number => {
  const months = DateTime.now().diff(datetime, "months").toObject().months;
  return months ? Math.floor(months) : 0;
};

/**
 * @description Returns the number of years elapsed since the provided datetime
 */
const getYearsSince = (datetime: DateTime): number => {
  const years = DateTime.now().diff(datetime, "years").toObject().years;
  return years ? Math.floor(years) : 0;
};

/**
 *
 * @param date
 * @returns date since in human readable format (e.g. 2 jours, 1 mois)
 */
export const getElapsedDurationLabel = (date: DateTime, elapsedPrefix = "Depuis") => {
  const numberOfDays = Math.floor(getDaysSince(date));
  const isToday = DateTime.now().hasSame(date, "day");

  if (isToday) {
    return "Aujourd'hui";
  }

  if (numberOfDays <= 30) {
    return `${elapsedPrefix} ${numberOfDays} ${handlePlural(numberOfDays, {
      plural: "jours",
      singular: "jour",
    })}`;
  }

  if (numberOfDays < 365) {
    const numberOfMonths = getMonthsSince(date);
    return `${elapsedPrefix} ${getMonthsSince(date)} ${handlePlural(numberOfMonths, {
      plural: "mois",
      singular: "mois",
    })}`;
  }

  const numberOfYears = getYearsSince(date);
  return `${elapsedPrefix} ${numberOfYears} ${handlePlural(numberOfYears, {
    plural: "ans",
    singular: "an",
  })}`;
};

export const addWorkingDays = (date: DateTime, days: number) => {
  let remainingDays = days;
  while (remainingDays > 0) {
    date = date.plus({ days: 1 });
    if (![6, 7].includes(date.weekday)) {
      remainingDays -= 1;
    }
  }
  return date;
};

export type Weekdays =
  | "lundi"
  | "mardi"
  | "mercredi"
  | "jeudi"
  | "vendredi"
  | "samedi"
  | "dimanche";

/**
 * @description Converts a week day to its ISO 8601 code.
 * From ISO 8601 : https://en.wikipedia.org/wiki/ISO_8601#Week_dates
 */
export const weekDay2IsoCode = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
};

/**
 * Formats a date range into a human-readable French string
 * @param startsOn - The start date of the range
 * @param endsOn - The end date of the range (optional)
 * @returns A formatted string representing the date range:
 * - For current periods with end date: "jusqu'au DD/MM/YY"
 * - For future periods with end date: "à partir du DD/MM/YY jusqu'au DD/MM/YY"
 * - For future periods without end date: "à partir du DD/MM/YY"
 * - For past periods without end date: empty string
 */
export const formatDateRange = (startsOn: DateTime, endsOn: DateTime | null): string => {
  const now = DateTime.now();
  const dateFormat = "dd/MM/yy";

  // Format for periods with end date
  if (endsOn) {
    // Check if the period is current (start date has passed, end date is in the future)
    if (startsOn <= now && endsOn >= now) {
      return `jusqu'au ${endsOn.toFormat(dateFormat)}`;
    }

    // Period is in the future
    return `à partir du ${startsOn.toFormat(dateFormat)} jusqu'au ${endsOn.toFormat(dateFormat)}`;
  }

  // Format for periods without end date
  if (startsOn > now) {
    return `à partir du ${startsOn.toFormat(dateFormat)}`;
  }

  // Start date has passed and no end date
  return "";
};

/** Compact format for select dropdown labels */
export const formatCompactDateRange = (
  startsOn: DateTime | null,
  endsOn: DateTime | null
): string => {
  const now = DateTime.now().startOf("day");
  const dateFormat = "dd/MM/yy";

  const startsInFuture = startsOn && startsOn.startOf("day") > now;

  if (startsInFuture && endsOn) {
    return `${startsOn.toFormat(dateFormat)} → ${endsOn.toFormat(dateFormat)}`;
  }
  if (startsInFuture) {
    return `à partir du ${startsOn.toFormat(dateFormat)}`;
  }
  if (endsOn) {
    return `jusqu'au ${endsOn.toFormat(dateFormat)}`;
  }
  return "";
};

type CreateMonthSelectorOptionsType = {
  order?: "asc" | "desc";
  valueDateTimeFormat?: string;
  labelDateTimeFormat?: string;
};

/**
 * Create month selector options for date range selection
 * @param startMonthInUTC - The starting month (DateTime in UTC timezone)
 * @param endMonthInUTC - The ending month (DateTime in UTC timezone)
 * @param options - Configuration options for formatting and ordering
 * @returns Array of options with value and label for month selection
 */
export const createMonthSelectorOptions = (
  startMonthInUTC: DateTime,
  endMonthInUTC: DateTime,
  options: CreateMonthSelectorOptionsType = {}
): { value: string; label: string }[] => {
  const {
    order = "asc",
    valueDateTimeFormat = "yyyy-MM-dd",
    labelDateTimeFormat = "MMM yyyy",
  } = options;

  let delta = startMonthInUTC.startOf("month");
  const end = endMonthInUTC.startOf("month");
  const monthOptions: { value: string; label: string }[] = [];

  while (delta <= end) {
    monthOptions.push({
      value: delta.toFormat(valueDateTimeFormat),
      label: upperFirst(delta.toFormat(labelDateTimeFormat)),
    });
    delta = delta.plus({ months: 1 });
  }

  return order === "desc" ? monthOptions.reverse() : monthOptions;
};
